// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiAddLine, RiFileCopyLine, RiSwapLine, RiCheckLine, RiEyeOffLine, RiEyeLine, RiExternalLink, RiRefreshLine } from 'react-icons/ri';
import { FiArrowUpRight, FiArrowDownLeft, FiSend, FiCopy, FiLogOut, FiSettings, FiPlusCircle, FiMenu, FiBell, FiExternalLink, FiGlobe, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useChain, NETWORK_CHANGE_EVENT } from '../context/ChainContext';
import { MAIN_ASSETS, POPULAR_BEP20_TOKENS } from '../constants/tokens';
import Header from './Header';
import Drawer from './Drawer';
import Footer from './Footer';
import TokenIcon from './TokenIcon';
import AppLoading from './AppLoading';
import '../styles/HomePage.css';
import { toast } from 'react-toastify';
import OnboardingModal from './OnboardingModal';
import EmptyState from './EmptyState';
import TransactionStatusIcon from './TransactionStatusIcon';
import LastUpdated from './LastUpdated';
import TooltipWrapper from './TooltipWrapper';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('assets');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isBalanceHidden, setIsBalanceHidden] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const contentRef = useRef(null);
  const startY = useRef(0);
  const navigate = useNavigate();
  const {
    wallet,
    chainWallets,
    selectedChain,
    chainBalances,
    transactions,
    loading,
    disconnect,
    tokenBalances,
    customTokens,
    detectedTokens,
    addCustomToken,
    tokenPrices,
    getTokenPrice,
    updateAllPrices,
    updateBalance,
    updateTokenBalances,
    getAdminTokens // <-- add this
  } = useWallet();
  const { chains, isTestnet } = useChain();
  const [priceCache, setPriceCache] = useState({});
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('hasSeenOnboarding'));
  const [localTransactions, setLocalTransactions] = useState(() => {
    try {
      const stored = localStorage.getItem('transactions');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sync localTransactions with context transactions if changed
  useEffect(() => {
    if (Array.isArray(transactions) && transactions.length > 0) {
      const stored = localStorage.getItem('transactions');
      const storedTxs = stored ? JSON.parse(stored) : [];
      // Only update if different
      const isDifferent = JSON.stringify(storedTxs) !== JSON.stringify(transactions);
      if (isDifferent) {
        localStorage.setItem('transactions', JSON.stringify(transactions));
        setLocalTransactions(transactions);
      }
    }
  }, [transactions]);

  // On mount, if context transactions is empty, use localTransactions
  useEffect(() => {
    if ((!transactions || transactions.length === 0) && localTransactions.length > 0) {
      // Optionally, you could set context transactions here if you have a setter
      // For now, just use localTransactions in the UI
    }
  }, [transactions, localTransactions]);

  const handleCloseOnboarding = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  // Add debugging for balance values
  useEffect(() => {
    console.log('HomePage - chainBalances:', chainBalances);
    console.log('HomePage - selectedChain:', selectedChain);
    console.log('HomePage - TON balance:', chainBalances?.TON);
    console.log('HomePage - wallet address:', wallet?.ton?.address);
    console.log('HomePage - tokenBalances:', tokenBalances);
  }, [chainBalances, selectedChain, wallet, tokenBalances]);

  // Add pull-to-refresh functionality
  // --- PULL TO REFRESH FUNCTIONALITY COMMENTED OUT ---
  /*
  const refreshThreshold = 100; // Threshold to trigger refresh in px

  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    toast.info('Refreshing data...', { autoClose: 1500 });
    
    try {
      // Update all data in parallel
      await Promise.all([
        fetchMainAssetPrices(),
        updateAllPrices && updateAllPrices(),
        wallet?.ton?.address && updateBalance(wallet.ton.address),
        wallet?.ton?.address && updateTokenBalances(wallet.ton.address)
      ]);
      
      toast.success('Data refreshed successfully!', { autoClose: 1500 });
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Refresh failed', { autoClose: 2000 });
    } finally {
      setRefreshing(false);
      setPullProgress(0);
    }
  };

  const handleTouchStart = (e) => {
    if (contentRef.current && contentRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (refreshing) return;
    
    if (contentRef.current && contentRef.current.scrollTop === 0 && startY.current !== null) {
      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;
      
      if (diff > 0) {
        // Calculate pull progress percentage (0-100)
        const progress = Math.min(diff / refreshThreshold * 100, 100);
        setPullProgress(progress);
        
        // Prevent default scrolling behavior when pulling down
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    if (refreshing) return;
    
    if (pullProgress >= 100) {
      // Trigger refresh if pulled past threshold
      handleRefresh();
    } else {
      // Reset progress if not pulled enough
      setPullProgress(0);
    }
    
    startY.current = null;
  };
  */
  // --- END PULL TO REFRESH FUNCTIONALITY ---

  const formatBalance = (balance) => {
    if (!balance && balance !== 0) return '0.00';
    const num = parseFloat(balance);
    if (isNaN(num)) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
      useGrouping: true
    }).format(num);
  };

  // Helper functions for localStorage caching
  function loadCachedMainAssetPrices() {
    try {
      const raw = localStorage.getItem('mainAssetPrices');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  function saveCachedMainAssetPrices(prices) {
    try {
      localStorage.setItem('mainAssetPrices', JSON.stringify(prices));
    } catch { }
  }
  function loadCachedChainBalances() {
    try {
      const raw = localStorage.getItem('chainBalances');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Check for expiry (90 seconds)
      if (parsed && parsed.timestamp && Date.now() - parsed.timestamp < 90000) {
        return parsed.balances;
      }
      return null;
    } catch {
      return null;
    }
  }
  function saveCachedChainBalances(balances) {
    try {
      localStorage.setItem('chainBalances', JSON.stringify({ balances, timestamp: Date.now() }));
    } catch { }
  }

  const [mainAssetPrices, setMainAssetPrices] = useState(
    loadCachedMainAssetPrices() || {
      TON: { price: 0, change24h: 0 },
      BNB: { price: 0, change24h: 0 },
      // TRG: { price: 0.06, change24h: 0 },
      XAU: { price: 2000, change24h: 0 }, // Placeholder price
      USDT: { price: 1, change24h: 0 },
      BUSD: { price: 1, change24h: 0 },
      BTCB: { price: 0, change24h: 0 }
    }
  );
  const [chainBalancesState, setChainBalancesState] = useState(loadCachedChainBalances() || {});

  // When updating balances (e.g., after send/receive), always fetch fresh if cache is expired or after action
  const updateBalancesWithExpiry = async () => {
    const cached = loadCachedChainBalances();
    if (!cached) {
      // Fetch fresh balances here (simulate with updateBalance, updateTokenBalances, etc.)
      await Promise.all([
        wallet?.ton?.address && updateBalance(wallet.ton.address),
        wallet?.ton?.address && updateTokenBalances(wallet.ton.address)
      ]);
    }
  };
  // Call updateBalancesWithExpiry after send/receive actions as needed

  const fetchMainAssetPrices = async () => {
    try {
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTime;

      // Cache for 60 seconds to avoid rate limiting
      if (timeSinceLastFetch < 60000 && Object.keys(priceCache).length > 0) {
        setMainAssetPrices(priceCache);
        saveCachedMainAssetPrices(priceCache);
        return;
      }

      setPricesLoading(true);
      // Get coingecko IDs for tokens
      const coingeckoIds = Object.values(MAIN_ASSETS)
        .map(token => {
          switch (token.symbol) {
            case 'TON':
              return 'the-open-network';
            case 'BNB':
              return 'binancecoin';
            case 'USDT':
              return 'tether';
            case 'USDC':
              return 'usd-coin';
            case 'TRG':
            //   return 'trigo';
            case 'XAU':
              return 'egold'; // Placeholder ID
            default:
              return null;
          }
        })
        .filter(id => id !== null)
        .join(',');

      if (!coingeckoIds) {
        console.warn('No valid CoinGecko IDs found');
        return;
      }

      const response = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${coingeckoIds}&vs_currencies=usd&include_24hr_change=true`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'User-Agent': 'Mozilla/5.0 (compatible; WalletApp/1.0)'
          },
          mode: 'cors'
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('CoinGecko rate limit reached, using cached prices');
          if (Object.keys(priceCache).length > 0) {
            setMainAssetPrices(priceCache);
          }
          return;
        }
        if (response.status === 403 || response.status === 0) {
          console.warn('CoinGecko CORS or access denied, using fallback prices');
          // Use fallback prices when API is blocked
          const fallbackPrices = {
            TON: { price: 2.5, change24h: 0 },
            BNB: { price: 300, change24h: 0 },
            USDT: { price: 1, change24h: 0 },
            USDC: { price: 1, change24h: 0 },
            // TRG: { price: 0.06, change24h: 0 }
            XAU: { price: 2000, change24h: 0 }
          };
          setMainAssetPrices(fallbackPrices);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the response into our price format
      const newPrices = {};

      // Handle TON price
      if (data['the-open-network']) {
        newPrices.TON = {
          price: data['the-open-network'].usd,
          change24h: data['the-open-network'].usd_24h_change || 0
        };
      }

      // Handle BNB price
      if (data['binancecoin']) {
        newPrices.BNB = {
          price: data['binancecoin'].usd,
          change24h: data['binancecoin'].usd_24h_change || 0
        };
      }

      // Handle TRG price -> COMMENTED OUT
      /*
      if (data['trigo']) {
        newPrices.TRG = {
          price: data['trigo'].usd,
          change24h: data['trigo'].usd_24h_change || 0
        };
      }
      */
      // Handle XAU price (EGold)
      if (data['egold']) {
        newPrices.XAU = {
          price: data['egold'].usd,
          change24h: data['egold'].usd_24h_change || 0
        };
      } else {
        // Fallback if API missing
        newPrices.XAU = { price: 2000, change24h: 0 };
      }

      // Handle USDT price
      if (data['tether']) {
        newPrices.USDT = {
          price: data['tether'].usd,
          change24h: data['tether'].usd_24h_change || 0
        };
      }
      // Handle USDC price
      if (data['usd-coin']) {
        newPrices.USDC = {
          price: data['usd-coin'].usd,
          change24h: data['usd-coin'].usd_24h_change || 0
        };
      }
      // --- Force USDT/USDC to always be $1.00 and 0% change ---
      newPrices.USDT = { price: 1, change24h: 0 };
      newPrices.USDC = { price: 1, change24h: 0 };

      // Update the cache and last fetch time
      setPriceCache(newPrices);
      setLastFetchTime(now);
      setMainAssetPrices(newPrices);
      setError('');
      saveCachedMainAssetPrices(newPrices);
    } catch (err) {
      console.error('Error fetching prices:', err);
      // Use cached prices if available when an error occurs
      const cached = loadCachedMainAssetPrices();
      if (cached) {
        setMainAssetPrices(cached);
      }
      setError('Failed to update prices');
    } finally {
      setPricesLoading(false);
    }
  };

  useEffect(() => {
    if (!wallet && !loading) {
      navigate('/welcome');
    }
  }, [wallet, loading, navigate]);

  // Add focus and visibility handlers
  useEffect(() => {
    const handleFocus = () => {
      fetchMainAssetPrices();
      if (updateAllPrices) {
        updateAllPrices();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchMainAssetPrices();
        if (updateAllPrices) {
          updateAllPrices();
        }
      }
    };

    // Update on mount and visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Initial fetch
    fetchMainAssetPrices();
    if (updateAllPrices) {
      updateAllPrices();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedChain, updateAllPrices]);

  // Keep the existing update interval
  useEffect(() => {
    const priceInterval = setInterval(fetchMainAssetPrices, 300000); // 5 minutes
    const balanceInterval = updateAllPrices ? setInterval(updateAllPrices, 15000) : null;

    return () => {
      clearInterval(priceInterval);
      if (balanceInterval) {
        clearInterval(balanceInterval);
      }
    };
  }, [selectedChain, updateAllPrices]);

  // Whenever chainBalances changes, cache it
  useEffect(() => {
    if (chainBalances) {
      setChainBalancesState(chainBalances);
      saveCachedChainBalances(chainBalances);
    }
  }, [chainBalances]);

  // On mount, if no balances in state, load from cache
  useEffect(() => {
    if (!chainBalances || Object.keys(chainBalances).length === 0) {
      const cached = loadCachedChainBalances();
      if (cached) setChainBalancesState(cached);
    }
  }, []);

  const handleCopyAddress = async () => {
    if (wallet?.address) {
      try {
        await navigator.clipboard.writeText(wallet.address);
        setCopySuccess(true);
        toast.success('Address copied to clipboard!');
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy address:', err);
        toast.error('Failed to copy address');
      }
    }
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatUSD = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      notation: 'standard'
    }).format(amount);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleDisconnect = () => {
    disconnect();
    navigate('/welcome');
  };

  const TokenIcon = ({ token, className }) => {
    const [imageError, setImageError] = useState(false);

    if (!imageError && token.image) {
      return (
        <div className={`token-icon ${className || ''}`}>
          <img
            src={token.image}
            alt={token.symbol}
            onError={() => setImageError(true)}
          />
        </div>
      );
    }

    // Fallback to default icon or generated avatar
    if (token.fallbackImage && !imageError) {
      return (
        <div className={`token-icon ${className || ''}`}>
          <img
            src={token.fallbackImage}
            alt={token.symbol}
            onError={() => setImageError(true)}
          />
        </div>
      );
    }

    // Final fallback to symbol
    return (
      <div className={`token-icon ${className || ''} ${token.iconClass || ''}`}>
        {token.icon || token.symbol?.charAt(0)}
      </div>
    );
  };

  // Memoize grouped transactions
  const groupedTransactions = useMemo(() => {
    if (!transactions.length) return {};
    return transactions.reduce((groups, tx) => {
      if (!tx || !wallet) return groups;
      try {
        const dateKey = tx.formattedDate || (tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleDateString() : '');
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(tx);
        return groups;
      } catch (error) {
        return groups;
      }
    }, {});
  }, [transactions, wallet]);

  // Update the totalBalance calculation to use mainAssetPrices
  const totalBalance = useMemo(() => {
    if (!wallet || !mainAssetPrices) return 0;

    let total = 0;

    // Always include TON and BNB, regardless of selectedChain
    const tonBalance = parseFloat(chainBalancesState?.TON || '0');
    const tonPrice = mainAssetPrices.TON?.price || 0;
    total += tonBalance * tonPrice;

    const bnbBalance = parseFloat(chainBalancesState?.BNB || '0');
    const bnbPrice = mainAssetPrices.BNB?.price || 0;
    total += bnbBalance * bnbPrice;

    // Add TRG balance if exists -> COMMENTED OUT
    // const trgBalance = parseFloat(tokenBalances['TRG']?.balance || tokenBalances['TRG']?.data?.balance || '0');
    // const trgPrice = mainAssetPrices.TRG?.price || 0;
    // total += trgBalance * trgPrice;

    // Add XAU balance if exists
    const xauBalance = parseFloat(tokenBalances['XAU']?.balance || tokenBalances['XAU']?.data?.balance || '0');
    const xauPrice = mainAssetPrices.XAU?.price || 0;
    total += xauBalance * xauPrice;

    // Add USDT balance if exists
    const usdtBalance = parseFloat(tokenBalances['USDT']?.balance || tokenBalances['USDT']?.data?.balance || '0');
    const usdtPrice = mainAssetPrices.USDT?.price || 1;
    total += usdtBalance * usdtPrice;

    // Add USDC balance if exists
    const usdcBalance = parseFloat(tokenBalances['USDC']?.balance || tokenBalances['USDC']?.data?.balance || '0');
    const usdcPrice = mainAssetPrices.USDC?.price || 1;
    total += usdcBalance * usdcPrice;

    return total;
  }, [chainBalancesState, tokenBalances, mainAssetPrices, wallet]);

  // Add chain-specific address display
  const getChainAddress = (chain) => {
    return chainWallets[chain]?.address || 'Not connected';
  };

  // Add chain-specific explorer URL
  const getExplorerUrl = (chain) => {
    const chainConfig = chains[chain];
    return isTestnet ? chainConfig.testnet.explorer : chainConfig.mainnet.explorer;
  };

  const getTokenBalance = (token) => {
    if (!token) return '0';

    const network = isTestnet ? 'testnet' : 'mainnet';

    if (selectedChain === 'TON') {
      // For TON tokens, use the symbol as key if it's a native token
      if (token.isNative) {
        return chainBalances[token.symbol] || '0';
      }

      // Use the network-specific address
      const tokenAddress = token[`${network}Address`] || token.address;
      return tokenBalances[tokenAddress]?.data?.balance ||
        tokenBalances[token.symbol]?.data?.balance || '0';
    } else if (selectedChain === 'BNB') {
      // For BNB native token
      if (token.isNative) {
        return chainBalances[token.symbol] || '0';
      }

      // For BNB tokens, use the token address for the current network
      const tokenAddress = token[`${network}Address`] || token.address;
      return tokenBalances[tokenAddress]?.data?.balance || '0';
    }

    return '0';
  };

  // Add a useEffect to refresh balances when network changes
  useEffect(() => {
    // Add a function to handle network changes
    const handleNetworkChange = (event) => {
      console.log('HomePage detected network change:', isTestnet ? 'testnet' : 'mainnet');

      // Update UI immediately, even if the page will later reload
      try {
        // Clear any cached balances
        if (wallet?.address) {
          // Force re-render with new network
          setError(`Switching to ${isTestnet ? 'Testnet' : 'Mainnet'}...`);
          setTimeout(() => setError(''), 1000);
        }
      } catch (err) {
        console.error('Error handling network change in HomePage:', err);
      }
    };

    // Listen for network change events
    window.addEventListener(NETWORK_CHANGE_EVENT, handleNetworkChange);

    // Cleanup
    return () => {
      window.removeEventListener(NETWORK_CHANGE_EVENT, handleNetworkChange);
    };
  }, [isTestnet, wallet]);

  // Custom SVG icons for actions (Premium/Modern)
  const SendIcon = (props) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M21.6 11.125L13.1 2.625C12.5 2.025 11.5 2.025 10.9 2.625L2.40002 11.125C1.80002 11.725 1.80002 12.725 2.40002 13.325C3.00002 13.925 4.00002 13.925 4.60002 13.325L10.5 7.425V20.5C10.5 21.3 11.2 22 12 22C12.8 22 13.5 21.3 13.5 20.5V7.425L19.4 13.325C20 13.925 21 13.925 21.6 13.325C22.2 12.725 22.2 11.725 21.6 11.125Z" fill="currentColor" />
    </svg>
  );

  const ReceiveIcon = (props) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M12 21.25C11.575 21.25 11.25 20.925 11.25 20.5V11.5C11.25 11.075 11.575 10.75 12 10.75C12.425 10.75 12.75 11.075 12.75 11.5V20.5C12.75 20.925 12.425 21.25 12 21.25Z" fill="currentColor" />
      <path d="M12 22C11.575 22 11.175 21.825 10.875 21.525L7.375 18.025C6.925 17.575 6.925 16.875 7.375 16.425C7.825 15.975 8.525 15.975 8.975 16.425L12 19.45L15.025 16.425C15.475 15.975 16.175 15.975 16.625 16.425C17.075 16.875 17.075 17.575 16.625 18.025L13.125 21.525C12.825 21.825 12.425 22 12 22Z" fill="currentColor" />
      <path d="M18.525 2H5.475C3.55 2 2 3.55 2 5.475V11.525C2 13.45 3.55 15 5.475 15H18.525C20.45 15 22 13.45 22 11.525V5.475C22 3.55 20.45 2 18.525 2Z" fill="currentColor" fillOpacity={0.4} />
    </svg>
  );

  const SwapIcon = (props) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M4 7h13M17 7l-3-3m3 3l-3 3M20 17H7m-3 0l3 3m-3-3l3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const renderMainAssets = () => {
    return (
      <div className="main-assets">
        {/* TON Chain */}
        <div className="chain-section">
          {/* TON Native Token */}
          <div className="asset-item native" onClick={() => navigate(`/token/TON`, {
            state: {
              token: MAIN_ASSETS.TON,
              address: wallet?.ton?.address,
              chain: 'TON',
              isNative: true
            }
          })}>
            <div className="asset-info">
              <TokenIcon token={MAIN_ASSETS.TON} />
              <div className="asset-details">
                <div className="asset-name-row">
                  <span className="name">{MAIN_ASSETS.TON.name}</span>
                  <span className="chain-badge">TON</span>
                </div>
                <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(chainBalancesState?.TON || '0')} TON`}</span>
                <div className="asset-address">
                  <span className="address">{formatAddress(wallet?.ton?.address)}</span>
                  <a href={`${getExplorerUrl('TON')}/address/${wallet?.ton?.address}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                </div>
              </div>
            </div>
            <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
              <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(chainBalancesState?.TON || '0') * (mainAssetPrices.TON?.price || 0)).toFixed(2)}</span>
              <div className="asset-right-row-bottom">
                <span className="price">${(mainAssetPrices.TON?.price || 0).toFixed(2)}</span>
                <span className={`price-change ${mainAssetPrices.TON?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices.TON?.change24h >= 0 ? '+' : ''}{(mainAssetPrices.TON?.change24h || 0).toFixed(2)}%</span>
              </div>
              <div className="asset-actions-row" style={{ gap: 6 }}>
                <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
              </div>
            </div>
          </div>

          {/* TON Tokens - XAU platform token at the top */}
          {['XAU', 'USDT', 'USDC'].map(symbol => {
            const token = MAIN_ASSETS[symbol];
            if (!token) return null;
            const balance = tokenBalances[symbol]?.balance || tokenBalances[symbol]?.data?.balance || '0';
            const price = mainAssetPrices[symbol]?.price || 0;
            const usdValue = parseFloat(balance) * price;
            const tokenAddress = isTestnet ? token.testnetAddress : token.mainnetAddress;

            return (
              <div key={symbol} className="asset-item" onClick={() => navigate(`/token/${symbol}`, {
                state: {
                  token: token,
                  address: tokenAddress,
                  chain: 'TON',
                  isNative: false
                }
              })}>
                <div className="asset-info">
                  <TokenIcon token={token} />
                  <div className="asset-details">
                    <div className="asset-name-row">
                      <span className="name">{token.name}</span>
                      <span className="chain-badge">TON</span>
                    </div>
                    <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(balance)} ${symbol}`}</span>
                    <div className="asset-address">
                      <span className="address">{formatAddress(tokenAddress)}</span>
                      <a href={`${getExplorerUrl('TON')}/token/${tokenAddress}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                    </div>
                  </div>
                </div>
                <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                  <span className="value" style={{ fontWeight: 700 }}>≈ ${usdValue.toFixed(2)}</span>
                  <div className="asset-right-row-bottom">
                    <span className="price">${price.toFixed(2)}</span>
                    <span className={`price-change ${mainAssetPrices[symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices[symbol]?.change24h >= 0 ? '+' : ''}{(mainAssetPrices[symbol]?.change24h || 0).toFixed(2)}%</span>
                  </div>
                  <div className="asset-actions-row" style={{ gap: 6 }}>
                    <motion.button className="asset-action receive" onClick={(e) => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                    <motion.button className="asset-action send" onClick={(e) => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* BNB Chain */}
        <div className="chain-section">
          {/* BNB Native Token */}
          <div className="asset-item native" onClick={() => navigate(`/token/BNB`, {
            state: {
              token: MAIN_ASSETS.BNB,
              address: chainWallets.BNB?.address,
              chain: 'BNB',
              isNative: true
            }
          })}>
            <div className="asset-info">
              <TokenIcon token={MAIN_ASSETS.BNB} />
              <div className="asset-details">
                <div className="asset-name-row">
                  <span className="name">{MAIN_ASSETS.BNB.name}</span>
                  <span className="chain-badge">BNB</span>
                </div>
                <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(chainBalancesState?.BNB || '0')} BNB`}</span>
                <div className="asset-address">
                  <span className="address">{formatAddress(chainWallets.BNB?.address)}</span>
                  <a href={`${getExplorerUrl('BNB')}/address/${chainWallets.BNB?.address}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                </div>
              </div>
            </div>
            <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
              <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(chainBalancesState?.BNB || '0') * (mainAssetPrices.BNB?.price || 0)).toFixed(2)}</span>
              <div className="asset-right-row-bottom">
                <span className="price">${(mainAssetPrices.BNB?.price || 0).toFixed(2)}</span>
                <span className={`price-change ${mainAssetPrices.BNB?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices.BNB?.change24h >= 0 ? '+' : ''}{(mainAssetPrices.BNB?.change24h || 0).toFixed(2)}%</span>
              </div>
              <div className="asset-actions-row" style={{ gap: 6 }}>
                <motion.button className="asset-action receive" onClick={(e) => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                <motion.button className="asset-action send" onClick={(e) => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
              </div>
            </div>
          </div>

          {/* BEP20 Tokens */}
          {POPULAR_BEP20_TOKENS[isTestnet ? 'testnet' : 'mainnet']
            .filter(token => {
              // Only show tokens that have an address for the current network
              const network = isTestnet ? 'testnet' : 'mainnet';
              return token[`${network}Address`] || token.address;
            })
            .map(token => {
              const network = isTestnet ? 'testnet' : 'mainnet';
              const tokenAddress = token[`${network}Address`] || token.address;
              const balance = tokenBalances[tokenAddress]?.balance || tokenBalances[tokenAddress]?.data?.balance || '0';
              const price = tokenPrices[token.symbol]?.price || token.defaultPrice || 0;
              const usdValue = parseFloat(balance) * price;

              // Skip tokens with zero balance and missing address for current network
              if (parseFloat(balance) === 0 && !token.alwaysShow) {
                return null;
              }

              return (
                <div key={tokenAddress} className="asset-item" onClick={() => navigate(`/token/${token.symbol}`, {
                  state: {
                    token: token,
                    address: tokenAddress,
                    chain: 'BNB',
                    isNative: false
                  }
                })}>
                  <div className="asset-info">
                    <TokenIcon token={{
                      ...token,
                      image: `/assets/tokens/${token.symbol.toLowerCase()}.png`,
                      fallbackImage: `https://ui-avatars.com/api/?name=${token.symbol}&background=F3BA2F&color=fff&size=128`
                    }} />
                    <div className="asset-details">
                      <div className="asset-name-row">
                        <span className="name">{token.name}</span>
                        <span className="chain-badge">BNB</span>
                      </div>
                      <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(balance)} ${token.symbol}`}</span>
                      <div className="asset-address">
                        <span className="address">{formatAddress(tokenAddress)}</span>
                        <a href={`${getExplorerUrl('BNB')}/token/${tokenAddress}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                      </div>
                    </div>
                  </div>
                  <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                    <span className="value" style={{ fontWeight: 700 }}>≈ ${usdValue.toFixed(2)}</span>
                    <div className="asset-right-row-bottom">
                      <span className="price">${price.toFixed(2)}</span>
                      <span className={`price-change ${tokenPrices[token.symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{tokenPrices[token.symbol]?.change24h >= 0 ? '+' : ''}{(tokenPrices[token.symbol]?.change24h || 0).toFixed(2)}%</span>
                    </div>
                    <div className="asset-actions-row" style={{ gap: 6 }}>
                      <motion.button className="asset-action receive" onClick={(e) => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                      <motion.button className="asset-action send" onClick={(e) => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>
    );
  };

  if (!wallet && !loading) {
    return null;
  }

  // Update the history section to use grouped transactions
  const renderTransactionList = () => {
    const txs = (transactions && transactions.length > 0) ? transactions : localTransactions;
    if (!txs.length) {
      return (
        <EmptyState
          image="/assets/empty-tx.svg"
          title="No Transactions"
          description="You have no transaction history yet. Send or receive to see your activity here."
          action
          actionLabel="Send"
          onAction={() => navigate('/send')}
        />
      );
    }
    return (
      <div className="transactions-list">
        {txs.map((tx, index) => (
          <motion.div
            key={(tx.hash || tx.id || tx.timestamp) + '-' + index}
            className={`transaction-item ${tx.type} ${tx.type === 'admin' ? 'admin-tx' : ''}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.2 }}
            whileHover={{ x: 3 }}
          >
            <TransactionStatusIcon status={tx.status || 'confirmed'} size={18} />
            <div className={`transaction-icon ${tx.type}`}>
              {tx.type === 'send' ? <FiSend /> : tx.type === 'admin' ? <FiExternalLink /> : <FiArrowDownLeft />}
            </div>
            <div className="transaction-details">
              <div className="transaction-main">
                <span className="type">
                  {tx.type === 'send' ? 'Sent' : tx.type === 'receive' ? 'Received' : tx.type === 'admin' ? 'Admin' : 'Token'}
                </span>
                <span className={`amount ${tx.type}`}>
                  {tx.type === 'admin' ? tx.formattedAmount : tx.formattedAmount} {tx.symbol}
                </span>
              </div>
              <div className="transaction-sub">
                <span><FiClock size={12} /> {tx.formattedDate} {tx.formattedTime}</span>
                <span>
                  {tx.type === 'send' ? (
                    <>To: <span className="transaction-address">{tx.to}</span></>
                  ) : tx.type === 'admin' ? (
                    <span className="transaction-chain">{tx.chain}</span>
                  ) : (
                    <>From: <span className="transaction-address">{tx.from}</span></>
                  )}
                </span>
              </div>
            </div>
            {tx.hash ? (
              <a href={tx.explorerUrl} target="_blank" rel="noopener noreferrer" className="view-button">
                <FiExternalLink />
              </a>
            ) : (
              <span className="view-button disabled" title="Not yet on-chain">
                <FiExternalLink style={{ opacity: 0.4, cursor: 'not-allowed' }} />
              </span>
            )}
          </motion.div>
        ))}
      </div>
    );
  };

  // Helper to check if a token has a real logo (not just a fallback)
  const hasRealLogo = (token) => token.image && (!token.fallbackImage || token.image !== token.fallbackImage);

  return (
    <>
      {showOnboarding && (
        <OnboardingModal onClose={handleCloseOnboarding} />
      )}
      <div className="home-page">
        <Header
          isHome={true}
          onMenuClick={() => setIsDrawerOpen(true)}
        />

        <div
          className="content"
          ref={contentRef}
        // onTouchStart={handleTouchStart}
        // onTouchMove={handleTouchMove}
        // onTouchEnd={handleTouchEnd}
        >
          {/* Pull to refresh indicator */}
          {/* <div className={`pull-to-refresh-indicator ${pullProgress > 0 ? 'visible' : ''}`}>
            <div className="pull-icon" style={{ 
              transform: `rotate(${pullProgress * 3.6}deg)`,
              opacity: pullProgress / 100 
            }}>
              <RiRefreshLine 
                className={refreshing ? 'rotating' : ''} 
                size={24} 
              />
            </div>
            <span className="pull-text">
              {refreshing 
                ? 'Refreshing...' 
                : pullProgress >= 100 
                  ? 'Release to refresh' 
                  : 'Pull to refresh'}
            </span>
          </div> */}

          {loading ? (
            <AppLoading text="Loading wallet..." overlay={true} />
          ) : (
            <>
              <motion.div
                className="wallet-card modern-glass"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                tabIndex={0}
                aria-label="Wallet overview"
              >
                <motion.div
                  className="floating-particles"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.5 }}
                >
                  {[...Array(10)].map((_, index) => (
                    <div key={index} className="particle" />
                  ))}
                </motion.div>

                <div className="card-header">
                  <div className="card-title">
                    <div className="title-left">
                      <span className="balance-label">Balance</span>
                      {isTestnet && <span className="testnet-badge">Testnet</span>}
                    </div>
                    <div className="title-right">
                      <div className="card-address">
                        {formatAddress(wallet.address)}
                        <button
                          className="visibility-toggle"
                          onClick={() => setIsBalanceHidden(!isBalanceHidden)}
                          title={isBalanceHidden ? 'Show Balance' : 'Hide Balance'}
                          aria-label={isBalanceHidden ? 'Show Balance' : 'Hide Balance'}
                        >
                          {isBalanceHidden ? <RiEyeLine /> : <RiEyeOffLine />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="balance-display">
                  <div className="total-balance">
                    <h2>Total Balance</h2>
                    <div className="amount-row">
                      <TooltipWrapper content="Your total wallet value in USD. Updated in real time.">
                        <div className="amount">
                          {isBalanceHidden
                            ? '••••••'
                            : formatUSD(totalBalance)
                          }
                        </div>
                      </TooltipWrapper>
                    </div>
                  </div>
                </div>
                <LastUpdated timestamp={lastFetchTime} />
                <div className="card-actions modern-actions">
                  <TooltipWrapper content="Receive TON or tokens to your wallet.">
                    <motion.button
                      className="action-button receive modern-action"
                      onClick={() => navigate('/receive')}
                      whileHover={{ scale: 1.12, boxShadow: '0 0 12px #1a73e8' }}
                      whileTap={{ scale: 0.96 }}
                      aria-label="Receive"
                    >
                      <ReceiveIcon style={{ marginRight: 8 }} />
                      <span>Receive</span>
                    </motion.button>
                  </TooltipWrapper>
                  <TooltipWrapper content="Send TON or tokens to another wallet.">
                    <motion.button
                      className="action-button send modern-action"
                      onClick={() => navigate('/send')}
                      whileHover={{ scale: 1.12, boxShadow: '0 0 12px #1a73e8' }}
                      whileTap={{ scale: 0.96 }}
                      aria-label="Send"
                    >
                      <SendIcon style={{ marginRight: 8 }} />
                      <span>Send</span>
                    </motion.button>
                  </TooltipWrapper>

                </div>
              </motion.div>

              <AnimatePresence>
                {copySuccess && (
                  <motion.div
                    className="copy-success"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    Address copied!
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="tabs">
                <button
                  className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
                  onClick={() => setActiveTab('assets')}
                >
                  Assets
                </button>
                <button
                  className={`tab ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  History
                </button>
                <div className="tab-actions">
                  <motion.button
                    className="add-token-button-tiny"
                    onClick={() => navigate('/add-custom-token')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Add Token"
                    aria-label="Add Token"
                  >
                    <FiPlusCircle size={16} />
                  </motion.button>
                </div>
              </div>

              {activeTab === 'assets' ? (
                <motion.div
                  className="assets-list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="assets-section">
                    {/* Native TON */}
                    <div className="asset-item native" onClick={() => navigate(`/token/TON`, {
                      state: { token: MAIN_ASSETS.TON, address: wallet?.ton?.address, chain: 'TON', isNative: true }
                    })}>
                      <div className="asset-info">
                        <TokenIcon token={MAIN_ASSETS.TON} />
                        <div className="asset-details">
                          <div className="asset-name-row">
                            <span className="name">{MAIN_ASSETS.TON.name}</span>
                            <span className="chain-badge">TON</span>
                          </div>
                          <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(chainBalancesState?.TON || '0')} TON`}</span>
                          <div className="asset-address">
                            <span className="address">{formatAddress(wallet?.ton?.address)}</span>
                            <a href={`${getExplorerUrl('TON')}/address/${wallet?.ton?.address}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                          </div>
                        </div>
                      </div>
                      <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                        <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <span className="price">${(mainAssetPrices.TON?.price || 0).toFixed(2)}</span>
                          <span className={`price-change ${mainAssetPrices.TON?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices.TON?.change24h >= 0 ? '+' : ''}{(mainAssetPrices.TON?.change24h || 0).toFixed(2)}% {mainAssetPrices.TON?.change24h >= 0 ? '▲' : '▼'}</span>
                        </div>
                        <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(chainBalancesState?.TON || '0') * (mainAssetPrices.TON?.price || 0)).toFixed(2)}</span>
                        <div className="asset-actions-row" style={{ gap: 6 }}>
                          <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                          <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                        </div>
                      </div>
                    </div>
                    {/* Native BNB */}
                    <div className="asset-item native" onClick={() => navigate(`/token/BNB`, {
                      state: { token: MAIN_ASSETS.BNB, address: chainWallets.BNB?.address, chain: 'BNB', isNative: true }
                    })}>
                      <div className="asset-info">
                        <TokenIcon token={MAIN_ASSETS.BNB} />
                        <div className="asset-details">
                          <div className="asset-name-row">
                            <span className="name">{MAIN_ASSETS.BNB.name}</span>
                            <span className="chain-badge">BNB</span>
                          </div>
                          <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(chainBalancesState?.BNB || '0')} BNB`}</span>
                          <div className="asset-address">
                            <span className="address">{formatAddress(chainWallets.BNB?.address)}</span>
                            <a href={`${getExplorerUrl('BNB')}/address/${chainWallets.BNB?.address}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                          </div>
                        </div>
                      </div>
                      <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                        <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <span className="price">${(mainAssetPrices.BNB?.price || 0).toFixed(2)}</span>
                          <span className={`price-change ${mainAssetPrices.BNB?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices.BNB?.change24h >= 0 ? '+' : ''}{(mainAssetPrices.BNB?.change24h || 0).toFixed(2)}% {mainAssetPrices.BNB?.change24h >= 0 ? '▲' : '▼'}</span>
                        </div>
                        <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(chainBalancesState?.BNB || '0') * (mainAssetPrices.BNB?.price || 0)).toFixed(2)}</span>
                        <div className="asset-actions-row" style={{ gap: 6 }}>
                          <motion.button className="asset-action receive" onClick={(e) => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                          <motion.button className="asset-action send" onClick={(e) => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                        </div>
                      </div>
                    </div>
                    {/* TON Jettons */}
                    {Object.entries(MAIN_ASSETS)
                      .filter(([symbol, token]) => token.chain === 'TON' && symbol !== 'TON')
                      .map(([symbol, token]) => {
                        const tokenAddress = isTestnet ? token.testnetAddress : token.mainnetAddress;
                        const tokenBalance = tokenBalances[symbol]?.balance || tokenBalances[symbol]?.data?.balance || '0';
                        return (
                          <div key={`TON-${symbol}`} className="asset-item" onClick={() => navigate(`/token/${symbol}`, {
                            state: { token, address: tokenAddress, chain: 'TON', isNative: false }
                          })}>
                            <div className="asset-info">
                              <TokenIcon token={token} />
                              <div className="asset-details">
                                <div className="asset-name-row">
                                  <span className="name">{token.name}</span>
                                  <span className="chain-badge">TON</span>
                                </div>
                                <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(tokenBalance)} ${symbol}`}</span>
                                <div className="asset-address">
                                  <span className="address">{formatAddress(tokenAddress)}</span>
                                  <a href={`${getExplorerUrl('TON')}/token/${tokenAddress}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                                </div>
                              </div>
                            </div>
                            <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                              <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                <span className="price">${(mainAssetPrices[symbol]?.price || 0).toFixed(2)}</span>
                                <span className={`price-change ${mainAssetPrices[symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{mainAssetPrices[symbol]?.change24h >= 0 ? '+' : ''}{(mainAssetPrices[symbol]?.change24h || 0).toFixed(2)}% {mainAssetPrices[symbol]?.change24h >= 0 ? '▲' : '▼'}</span>
                              </div>
                              <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(tokenBalance) * (mainAssetPrices[symbol]?.price || 0)).toFixed(2)}</span>
                              <div className="asset-actions-row" style={{ gap: 6 }}>
                                <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                                <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* BNB BEP20 Tokens */}
                    {POPULAR_BEP20_TOKENS[isTestnet ? 'testnet' : 'mainnet']
                      .map(token => {
                        const tokenBalance = tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0';
                        return (
                          <div key={`BNB-${token.symbol}-${token.address}`} className="asset-item" onClick={() => navigate(`/token/${token.symbol}`, {
                            state: { token, address: token.address, chain: 'BNB', isNative: false }
                          })}>
                            <div className="asset-info">
                              <TokenIcon token={token} />
                              <div className="asset-details">
                                <div className="asset-name-row">
                                  <span className="name">{token.name}</span>
                                  <span className="chain-badge">BNB</span>
                                </div>
                                <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(tokenBalance)} ${token.symbol}`}</span>
                                <div className="asset-address">
                                  <span className="address">{formatAddress(token.address)}</span>
                                  <a href={`${getExplorerUrl('BNB')}/token/${token.address}`} target="_blank" rel="noopener noreferrer" className="explorer-link" onClick={e => e.stopPropagation()}><FiExternalLink /></a>
                                </div>
                              </div>
                            </div>
                            <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                              <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                <span className="price">${(tokenPrices[token.symbol]?.price || token.defaultPrice || 0).toFixed(2)}</span>
                                <span className={`price-change ${tokenPrices[token.symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{tokenPrices[token.symbol]?.change24h >= 0 ? '+' : ''}{(tokenPrices[token.symbol]?.change24h || 0).toFixed(2)}% {tokenPrices[token.symbol]?.change24h >= 0 ? '▲' : '▼'}</span>
                              </div>
                              <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(tokenBalance) * (tokenPrices[token.symbol]?.price || token.defaultPrice || 0)).toFixed(2)}</span>
                              <div className="asset-actions-row" style={{ gap: 6 }}>
                                <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                                <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* Custom Tokens */}
                    {Object.values(customTokens).filter(token => hasRealLogo(token) || token.symbol === 'Not').map((token, idx) => {
                      const tokenBalance = tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0';
                      return (
                        <div key={`custom-${token.symbol}-${token.address}-${idx}`} className="asset-item custom" onClick={() => navigate(`/token/${token.symbol}`, {
                          state: { token, address: token.address, chain: token.chain, isCustom: true }
                        })}>
                          <div className="asset-info">
                            <TokenIcon token={token} />
                            <div className="asset-details">
                              <div className="asset-name-row">
                                <span className="name">{token.name}</span>
                                <span className="chain-badge">{token.chain}</span>
                              </div>
                              <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(tokenBalance)} ${token.symbol}`}</span>
                              <div className="asset-address">
                                <span className="address">{formatAddress(token.address)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                            <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                              <span className="price">${(tokenPrices[token.symbol]?.price || token.defaultPrice || 0).toFixed(2)}</span>
                              <span className={`price-change ${tokenPrices[token.symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{tokenPrices[token.symbol]?.change24h >= 0 ? '+' : ''}{(tokenPrices[token.symbol]?.change24h || 0).toFixed(2)}% {tokenPrices[token.symbol]?.change24h >= 0 ? '▲' : '▼'}</span>
                            </div>
                            <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(tokenBalance) * (tokenPrices[token.symbol]?.price || token.defaultPrice || 0)).toFixed(2)}</span>
                            <div className="asset-actions-row" style={{ gap: 6 }}>
                              <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                              <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Detected Tokens */}
                    {Object.values(detectedTokens).filter(token => hasRealLogo(token) || token.symbol === 'Not').map((token, idx) => {
                      const tokenBalance = tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0';
                      return (
                        <div key={`detected-${token.symbol}-${token.address}-${idx}`} className="asset-item detected" onClick={() => navigate(`/token/${token.symbol}`, {
                          state: { token, address: token.address, chain: token.chain, isDetected: true }
                        })}>
                          <div className="asset-info">
                            <TokenIcon token={token} />
                            <div className="asset-details">
                              <div className="asset-name-row">
                                <span className="name">{token.name}</span>
                                <span className="chain-badge">{token.chain}</span>
                              </div>
                              <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(tokenBalance)} ${token.symbol}`}</span>
                              <div className="asset-address">
                                <span className="address">{formatAddress(token.address)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                            <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                              <span className="price">${(tokenPrices[token.symbol]?.price || token.defaultPrice || 0).toFixed(2)}</span>
                              <span className={`price-change ${tokenPrices[token.symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{tokenPrices[token.symbol]?.change24h >= 0 ? '+' : ''}{(tokenPrices[token.symbol]?.change24h || 0).toFixed(2)}% {tokenPrices[token.symbol]?.change24h >= 0 ? '▲' : '▼'}</span>
                            </div>
                            <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(tokenBalance) * (tokenPrices[token.symbol]?.price || token.defaultPrice || 0)).toFixed(2)}</span>
                            <div className="asset-actions-row" style={{ gap: 6 }}>
                              <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                              <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Admin Tokens (TON/BNB only, filter out fallback-only except Notcoin) */}
                    {getAdminTokens().filter(token => hasRealLogo(token) || token.symbol === 'Not').map((token, idx) => {
                      const tokenBalance = tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0';
                      return (
                        <div key={`admin-${token.symbol}-${token.address}-${idx}`} className="asset-item admin" onClick={() => navigate(`/token/${token.symbol}`, {
                          state: { token, address: token.address, chain: token.chain, isAdmin: true }
                        })}>
                          <div className="asset-info">
                            <TokenIcon token={token} />
                            <div className="asset-details">
                              <div className="asset-name-row">
                                <span className="name">{token.name}</span>
                                <span className="chain-badge">{token.chain}</span>
                              </div>
                              <span className="balance">{isBalanceHidden ? '••••••' : `${formatBalance(tokenBalance)} ${token.symbol}`}</span>
                              <div className="asset-address">
                                <span className="address">{formatAddress(token.address)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="asset-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 110 }}>
                            <div className="asset-price-value" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                              <span className="price">${(tokenPrices[token.symbol]?.price || token.defaultPrice || 0).toFixed(2)}</span>
                              <span className={`price-change ${tokenPrices[token.symbol]?.change24h >= 0 ? 'positive' : 'negative'}`}>{tokenPrices[token.symbol]?.change24h >= 0 ? '+' : ''}{(tokenPrices[token.symbol]?.change24h || 0).toFixed(2)}% {tokenPrices[token.symbol]?.change24h >= 0 ? '▲' : '▼'}</span>
                            </div>
                            <span className="value" style={{ fontWeight: 700 }}>≈ ${(parseFloat(tokenBalance) * (tokenPrices[token.symbol]?.price || token.defaultPrice || 0)).toFixed(2)}</span>
                            <div className="asset-actions-row" style={{ gap: 6 }}>
                              <motion.button className="asset-action receive" onClick={e => { e.stopPropagation(); navigate('/receive'); }}><FiArrowDownLeft /></motion.button>
                              <motion.button className="asset-action send" onClick={e => { e.stopPropagation(); navigate('/send'); }}><FiSend /></motion.button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                renderTransactionList()
              )}
            </>
          )}
        </div>

        <Footer />

        <Drawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          title="Menu"
        >
          <div className="drawer-content">
            <button className="drawer-item" onClick={handleSettingsClick}>
              <FiSettings />
              <span>Settings</span>
            </button>
            <button className="drawer-item" onClick={handleDisconnect}>
              <FiLogOut />
              <span>Disconnect Wallet</span>
            </button>
          </div>
        </Drawer>
        {/* END of .home-page */}
      </div>
    </>
  );
};

export default HomePage;